import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "styles/ios-glass.css";

export const REFUND_RULE = "Refunds are possible until your first download or video play.";
const ACK = "mi.first-access.";
export const firstAccessDone = (key) => { try { return localStorage.getItem(ACK + key) === "1"; } catch { return false; } };
const remember = (key) => { try { localStorage.setItem(ACK + key, "1"); } catch { /* Asked again next visit. */ } };

// iOS-style alert. ask() resolves true (continue) or false (cancel).
export function useConfirm() {
  const [prompt, setPrompt] = useState(null);
  const resolver = useRef(null);
  const ask = useCallback((options) => new Promise((resolve) => { resolver.current = resolve; setPrompt(options); }), []);
  const close = (value) => { resolver.current?.(value); resolver.current = null; setPrompt(null); };
  const element = prompt ? <IosAlert {...prompt} onClose={close} /> : null;
  return [element, ask];
}

// Asks once per order/enrollment before the first download or play (refund rule, approved 7 Oct 2026).
export function useFirstAccessConfirm() {
  const [element, ask] = useConfirm();
  const confirm = useCallback(async (key) => {
    if (!key || firstAccessDone(key)) return true;
    const ok = await ask({
      title: "Start your first download?",
      message: "After this, the order can no longer be refunded. Continue?",
      confirm: "Continue",
    });
    if (ok) remember(key);
    return ok;
  }, [ask]);
  return [element, confirm];
}

function IosAlert({ title, message, confirm = "OK", cancel = "Cancel", destructive = false, onClose }) {
  const ok = useRef(null), closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const returnTo = document.activeElement;
    ok.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") closeRef.current(false); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); returnTo?.focus?.(); };
  }, []);
  // Portalled to <body>: a glass card (backdrop-filter, transform) would otherwise become the
  // containing block of the fixed scrim and the alert would cover only that card.
  return createPortal(
    <div className="ios-app ios-alert-scrim" role="presentation">
      <div className="ios-alert" role="alertdialog" aria-modal="true" aria-labelledby="ios-alert-title" aria-describedby="ios-alert-message">
        <h2 id="ios-alert-title">{title}</h2>
        <p id="ios-alert-message">{message}</p>
        <div className="ios-alert-actions">
          <button type="button" onClick={() => onClose(false)}>{cancel}</button>
          <button type="button" ref={ok} className={`ios-alert-primary${destructive ? " ios-alert-destructive" : ""}`} onClick={() => onClose(true)}>{confirm}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
